import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Dropdown } from "react-bootstrap";
import { DropdownBaseProps } from "react-bootstrap/lib/Dropdown";

import { useNavigate } from "react-router-dom";

import {
  ParticipantTypes,
  ResaleStatusReasons,
  ResaleStatuses,
  useSearchParticipantsQuery,
  useUpdateSingleResaleStatusMutation,
} from "@generated/graphql";

import { useIsPastReportPage } from "@components/detail_drawer/useIsPastReportPage";
import { getDisplayValueFromDBValue } from "@util/index";
import {
  deSerialiseOption,
  mapResaleStatusReasonsToDisplayFriendlyText,
  nonEligibleResaleStatuses,
  optedOutReasonOptions,
  remediateResaleStatusOptions,
  resaleStatusOptions,
  researchRequiredReasonOptions,
  serialiseOption,
} from "@util/resale";

import { ROUTES } from "../routes";

import ByPassHoldModal from "./ByPassHoldModal";
import { ToastMessage, type ToastMessageType } from "./ToastMessage";

interface ReportsToReviewDropdownProps extends DropdownBaseProps {
  resaleId: number;
  vendorId?: number;
  initialStatus: ResaleStatuses;
  isResaleLinkedToAnArtist: boolean;
  isDisabled?: boolean;
  onSuccess?: () => void;
  onLoading?: () => void;
  onDropdownClick?: (id: string | undefined) => void;
}

export const ResaleStatusDropDown: FC<ReportsToReviewDropdownProps> = ({
  resaleId,
  vendorId,
  initialStatus,
  isResaleLinkedToAnArtist,
  isDisabled,
  id,
  open,
  onSuccess,
  onLoading,
  onDropdownClick,
}) => {
  const [selectedOption, setSelectedOption] = useState(getDisplayValueFromDBValue(initialStatus));
  const [updateSingleResaleStatus, { loading }] = useUpdateSingleResaleStatusMutation();
  const [showModal, setShowModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ResaleStatusReasons>();
  const [selectedStatus, setSelectedStatus] = useState<ResaleStatuses>();
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState<ToastMessageType>();

  const { data: participantData } = useSearchParticipantsQuery({
    variables: {
      filter_by: [{ field: "participant_id", eq: vendorId?.toString() }],
    },
    skip: !vendorId,
  });
  const isPastReport = useIsPastReportPage();
  const participantType = useMemo(() => participantData?.searchParticipants?.[0]?.participant_types, [participantData]);
  const isNonEligibleStatus = useMemo(() => {
    return nonEligibleResaleStatuses.includes(initialStatus);
  }, [initialStatus]);
  useEffect(() => {
    setSelectedOption(getDisplayValueFromDBValue(initialStatus));
  }, [initialStatus]);
  const optionsToRender = useMemo(() => {
    let options = isNonEligibleStatus && isPastReport ? remediateResaleStatusOptions : resaleStatusOptions;

    if (initialStatus === ResaleStatuses.RESEARCH_REQUIRED) {
      options = options.filter((option) => option !== ResaleStatuses.RESEARCH_REQUIRED);
    }

    return options;
  }, [initialStatus, isNonEligibleStatus, isPastReport]);

  const filteredOptionsToRender = useMemo(
    () =>
      optionsToRender.filter(
        (option) => option !== ResaleStatuses.RESEARCH_REQUIRED && option !== ResaleStatuses.OPTED_OUT,
      ),
    [optionsToRender],
  );

  const shouldShowResearchRequired = useMemo(
    () => optionsToRender.includes(ResaleStatuses.RESEARCH_REQUIRED),
    [optionsToRender],
  );

  const shouldShowOptedOut = useMemo(() => optionsToRender.includes(ResaleStatuses.OPTED_OUT), [optionsToRender]);

  useEffect(() => {
    if (loading) {
      onLoading?.();
    }
  }, [loading, onLoading]);
  const updateResaleStatus = useCallback(
    async (status: ResaleStatuses, skipHoldPeriod: boolean, reason?: ResaleStatusReasons) => {
      try {
        const response = await updateSingleResaleStatus({
          variables: {
            input: {
              resale_id: resaleId,
              resale_status: status,
              resale_status_reason: reason,
              skip_hold_period: skipHoldPeriod,
            },
          },
        });

        const result = response?.data?.updateSingleResaleStatus;
        if (result?.success) {
          onSuccess?.();
          setSelectedOption(getDisplayValueFromDBValue(status));
          setShowModal(false);
          setToastMessage("Status updated successfully.");
          setToastMessageType("success");

          if (showModal && !skipHoldPeriod) {
            navigate(ROUTES.Dashboard.HoldPeriod.path);
          }
        } else {
          setToastMessage(result?.message || "Failed to update the status.");
          setToastMessageType("error");
        }
      } catch (error) {
        setToastMessage("An error occurred while updating the status. Please try again.");
        setToastMessageType("error");
      } finally {
        setShowToast(true);
      }
    },
    [updateSingleResaleStatus, resaleId, onSuccess, showModal, navigate],
  );
  const handleSelect = useCallback(
    (eventKey: string | null) => {
      if (eventKey) {
        const { status, reason } = deSerialiseOption(eventKey);

        if (!isResaleLinkedToAnArtist && status === ResaleStatuses.CONFIRMED_ELIGIBLE) {
          setToastMessage(
            "There is no existing artist linked to the resale, you are not allowed to update to 'Confirmed Eligible'.",
          );
          setShowToast(true);
          return;
        }

        setSelectedStatus(status);
        setSelectedReason(reason);
        const shouldShowModal =
          isPastReport &&
          (participantType?.includes(ParticipantTypes.AMP) || participantType?.includes(ParticipantTypes.ART_CENTRE)) &&
          (status === ResaleStatuses.CONFIRMED_ELIGIBLE || status === ResaleStatuses.RESEARCH_REQUIRED) &&
          !(initialStatus === ResaleStatuses.RESEARCH_REQUIRED && status === ResaleStatuses.CONFIRMED_ELIGIBLE);

        if (shouldShowModal) {
          setShowModal(true);
        } else {
          const skipHoldPeriod =
            participantType?.includes(ParticipantTypes.CMO) ||
            (initialStatus === ResaleStatuses.RESEARCH_REQUIRED && status === ResaleStatuses.CONFIRMED_ELIGIBLE);
          updateResaleStatus(status, skipHoldPeriod, reason);
        }
      }
    },
    [initialStatus, isPastReport, isResaleLinkedToAnArtist, participantType, updateResaleStatus],
  );
  const handleModalSubmit = (choice: "yes" | "no") => {
    if (selectedStatus) {
      const skipHoldPeriod = choice === "yes" || !!participantType?.includes(ParticipantTypes.CMO);
      updateResaleStatus(selectedStatus, skipHoldPeriod, selectedReason);
    }
  };

  // Stop event bubbling to row click
  const handleOnClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  // Manually control dropdown menu open status since react bootstrap autoClose is not working
  const handleOnToggle = useCallback(
    (metadataSource: string | undefined) => {
      if (metadataSource === "click") {
        onDropdownClick && onDropdownClick(id);
      } else if (metadataSource === "rootClose") {
        onDropdownClick && onDropdownClick(undefined);
      }
    },
    [id, onDropdownClick],
  );

  return (
    <>
      <ToastMessage
        message={toastMessage}
        show={showToast}
        setShow={setShowToast}
        delay={3000}
        type={toastMessageType}
      />
      <Dropdown
        autoClose
        show={open}
        onClick={handleOnClick}
        onSelect={handleSelect}
        onToggle={(_, meta) => {
          handleOnToggle(meta.source);
        }}
        className="customized-dropdown"
      >
        <Dropdown.Toggle
          disabled={isDisabled}
          id={id}
          variant="none"
          className={"customized-dropdown-toggle w-100 text-truncate"}
        >
          {selectedOption || "Select an option"}
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {filteredOptionsToRender.map((option) => (
            <Dropdown.Item key={option} eventKey={serialiseOption({ status: option })}>
              {getDisplayValueFromDBValue(option)}
            </Dropdown.Item>
          ))}
          {shouldShowResearchRequired && (
            <>
              <Dropdown.Header>Research required</Dropdown.Header>
              {researchRequiredReasonOptions.map((option) => (
                <Dropdown.Item
                  key={option}
                  eventKey={serialiseOption({ status: ResaleStatuses.RESEARCH_REQUIRED, reason: option })}
                >
                  {mapResaleStatusReasonsToDisplayFriendlyText(option)}
                </Dropdown.Item>
              ))}
            </>
          )}
          {shouldShowOptedOut && (
            <>
              <Dropdown.Header>Opt out</Dropdown.Header>
              {optedOutReasonOptions.map((option) => (
                <Dropdown.Item
                  key={option}
                  eventKey={serialiseOption({ status: ResaleStatuses.OPTED_OUT, reason: option })}
                >
                  {mapResaleStatusReasonsToDisplayFriendlyText(option)}
                </Dropdown.Item>
              ))}
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <ByPassHoldModal show={showModal} onHide={() => setShowModal(false)} onSubmit={handleModalSubmit} />
    </>
  );
};
